import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useCookieConsent } from '../contexts/CookieConsentContext';

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

/**
 * Loads the gtag script and initializes GA4 only if the user has accepted
 * analytics cookies (via the cookie consent banner). Google Analytics uses
 * cookies; we use it for analytics only, not for advertising.
 * Sends page_view on route changes when consent is given.
 */
export function GoogleAnalytics() {
  const location = useLocation();
  const { hasAnalyticsConsent } = useCookieConsent();
  const initialized = useRef(false);

  useEffect(() => {
    if (!GA_MEASUREMENT_ID || typeof window === 'undefined' || !hasAnalyticsConsent) return;

    try {
      const initGtag = () => {
        if (window.gtag) return;
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
        document.head.appendChild(script);
        window.dataLayer = window.dataLayer || [];
        window.gtag = function gtagShim() {
          window.dataLayer.push(arguments);
        };
        window.gtag('js', new Date());
        window.gtag('config', GA_MEASUREMENT_ID, {
          send_page_view: false,
          anonymize_ip: true,
        });
      };

      if (!initialized.current) {
        initGtag();
        initialized.current = true;
      }

      const pagePath = location.pathname + location.search;
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
          page_path: pagePath,
          page_title: document.title,
        });
      }
    } catch (e) {
      console.error('GoogleAnalytics init/page_view failed', e);
    }
  }, [hasAnalyticsConsent, location.pathname, location.search]);

  return null;
}
